/** The languages the add-ins are translated into. */
export type AppLanguage = "de" | "en";

/**
 * Where a language picked in the header is kept.
 *
 * In the session storage rather than the local one, which is the whole point of the switch: the
 * choice holds while someone works — including across page changes, which reload the add-in —
 * and is gone the next time they open the app, leaving them in the language their Geotab account
 * is set to. Reading the English original for a while costs nobody a settings change.
 */
const OVERRIDE_KEY = "languageOverride";

/**
 * The key the first version of the switch wrote into the local storage.
 *
 * It pinned the language for good. A value left over from then is dropped rather than read, or a
 * user who once looked at the English original would never see their own language again.
 */
const LEGACY_KEY = "language";

/**
 * The translation a language code asks for.
 *
 * Geotab accounts are not limited to the two languages the add-ins are translated into, so
 * anything that is not German is answered with the English original — it serves a French or
 * Polish user better than German does. An account with no language set at all is no answer and
 * leaves the choice to the caller.
 */
export function toAppLanguage(language: string | null | undefined): AppLanguage | null {
  if (!language) {
    return null;
  }

  return language.toLowerCase().startsWith("de") ? "de" : "en";
}

/** The language picked in the header in this browser tab, if there is one. */
export function readLanguageOverride(): AppLanguage | null {
  return toAppLanguage(sessionStorage.getItem(OVERRIDE_KEY));
}

/** Remembers a language picked in the header, for as long as this tab is open. */
export function writeLanguageOverride(language: AppLanguage): void {
  sessionStorage.setItem(OVERRIDE_KEY, language);
}

/** Clears what the earlier, permanent version of the switch left behind. */
export function dropLegacyLanguage(): void {
  localStorage.removeItem(LEGACY_KEY);
}
